/**
 * Knowledge-base suggestions panel — migration 077 (Stage-4 Artifact Loop).
 *
 * Renders the studio's pending `knowledge_base_suggestions` (drafted by the
 * `chat_config_optimizer_daily` cron's KB-mining pass for recurring unanswered
 * questions). Approve dispatches `apply_knowledge_base_entry` — which upserts the
 * `{topic, content}` into the LIVE `studio_config.knowledge_base` (no redeploy) —
 * and Reject dispatches `reject_knowledge_base_entry`; both as `human:staff`, the
 * SAME path a WhatsApp APPROVE/REJECT takes.
 *
 * Reads the pending list through the generic dispatch surface
 * (`list_pending_knowledge_base_entries`, a read action) rather than a dedicated
 * GET route — it's dispatched on mount and re-run after each mutation. Rendered
 * as a section on `/efficiency` and standalone at `/efficiency/knowledge`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  KnowledgeBaseSuggestionSummary,
  ListPendingKnowledgeBaseEntriesOutput,
} from "../lib/api";
import { useDispatch } from "../lib/use-dispatch";

/** Trim long draft content to a preview; the full text applies on approve. */
function preview(content: string, max = 240): string {
  const trimmed = content.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
}

export function KnowledgeBaseSuggestions(): JSX.Element {
  const [pending, setPending] = useState<
    ReadonlyArray<KnowledgeBaseSuggestionSummary>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const list = useDispatch<
    Record<string, never>,
    ListPendingKnowledgeBaseEntriesOutput
  >("list_pending_knowledge_base_entries");
  const apply = useDispatch<{ suggestion_id: string }, unknown>(
    "apply_knowledge_base_entry",
  );
  const reject = useDispatch<{ suggestion_id: string }, unknown>(
    "reject_knowledge_base_entry",
  );

  // `useDispatch.run` is memoised on the action name (stable across renders), so
  // `load` is stable too — the mount effect runs once.
  const listRun = list.run;
  const load = useCallback(async (): Promise<void> => {
    const r = await listRun({});
    if (cancelledRef.current) return;
    if (r.ok) {
      setPending(r.response.pending_suggestions);
      setError(null);
    } else {
      setError(r.message);
    }
    setLoading(false);
  }, [listRun]);

  useEffect(() => {
    cancelledRef.current = false;
    void load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  const onApprove = useCallback(
    async (id: string) => {
      await apply.run({ suggestion_id: id });
      await load();
    },
    [apply, load],
  );
  const onReject = useCallback(
    async (id: string) => {
      await reject.run({ suggestion_id: id });
      await load();
    },
    [reject, load],
  );

  const busy = apply.loading || reject.loading;
  const dispatchError = apply.error ?? reject.error;

  return (
    <section className="panel">
      <h2>Knowledge base suggestions</h2>
      <p className="member-note">
        Drafted by the <code>chat_config_optimizer_daily</code> miner from recurring
        questions the chat agent couldn&apos;t answer well. Approve to add the entry
        to the studio&apos;s live knowledge base (no redeploy); reject to dismiss.
      </p>
      {dispatchError !== null ? (
        <p
          className="tag tag-danger"
          style={{ display: "inline-block", marginBottom: 8 }}
        >
          {dispatchError.message}
        </p>
      ) : null}
      {error !== null ? (
        <p className="empty">Couldn&apos;t load knowledge suggestions ({error}).</p>
      ) : loading ? (
        <p className="empty">Loading knowledge suggestions…</p>
      ) : pending.length === 0 ? (
        <p className="empty">
          No pending knowledge suggestions. The miner runs daily and stages one here
          when it finds a recurring unanswered question.
        </p>
      ) : (
        pending.map((s) => (
          <div
            key={s.suggestion_id}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>{s.topic}</strong>
              <span className="subtle">{s.ref}</span>
            </div>
            <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{preview(s.content)}</p>
            <p className="member-note" style={{ marginTop: 4 }}>
              {s.rationale}
            </p>
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                disabled={busy}
                title="Add this entry to the live knowledge base"
                onClick={() => void onApprove(s.suggestion_id)}
                style={{
                  border: "1px solid #16a34a",
                  color: "#fff",
                  background: "#22975a",
                  borderRadius: 4,
                  padding: "4px 14px",
                  marginRight: 8,
                  cursor: busy ? "wait" : "pointer",
                  font: "inherit",
                }}
              >
                Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onReject(s.suggestion_id)}
                style={{
                  border: "1px solid #dc2626",
                  color: "#c0182f",
                  background: "none",
                  borderRadius: 4,
                  padding: "4px 14px",
                  cursor: busy ? "wait" : "pointer",
                  font: "inherit",
                }}
              >
                Reject
              </button>
            </div>
          </div>
        ))
      )}
    </section>
  );
}
